/**
 * TokenContext
 *
 * Gère globalement la balance de tokens de l'utilisateur
 * - Récupération automatique au login
 * - Mise à jour optimiste après transactions
 * - Synchronisation avec le backend
 */

import React, { createContext, useContext, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../utils/apiClient';

interface TokenContextType {
  balance: number;
  lifetimeSpent: number;
  lifetimeEarned: number;
  isLoading: boolean;
  error: string | null;
  fetchBalance: () => Promise<void>;
  deductTokens: (amount: number) => void;
  addTokens: (amount: number) => void;
  refreshBalance: () => Promise<void>;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const TokenProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [balance, setBalance] = useState<number>(0);
  const [lifetimeSpent, setLifetimeSpent] = useState<number>(0);
  const [lifetimeEarned, setLifetimeEarned] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Récupérer la balance depuis l'API
   */
  const fetchBalance = useCallback(async () => {
    if (!user || !isAuthenticated) {
      // Reset si pas authentifié
      setBalance(0);
      setLifetimeSpent(0);
      setLifetimeEarned(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient('/tokens/balance');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch token balance`);
      }

      const data = await response.json();

      setBalance(data.balance || 0);
      setLifetimeSpent(data.lifetimeSpent || 0);
      setLifetimeEarned(data.lifetimeEarned || 0);

    } catch (err) {
      console.error('Error fetching token balance:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');

      // Ne pas reset la balance en cas d'erreur (garder valeur cache)
      // setBalance(0);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  /**
   * Déduire des tokens de manière optimiste (avant confirmation API)
   * Utilisé après un appel IA réussi
   */
  const deductTokens = useCallback((amount: number) => {
    setBalance(prev => Math.max(0, prev - amount));
    setLifetimeSpent(prev => prev + amount);
  }, []);

  /**
   * Ajouter des tokens de manière optimiste
   * Utilisé après une recharge ou un bonus
   */
  const addTokens = useCallback((amount: number) => {
    setBalance(prev => prev + amount);
    setLifetimeEarned(prev => prev + amount);
  }, []);

  /**
   * Force refresh de la balance (bypasse cache)
   */
  const refreshBalance = useCallback(async () => {
    await fetchBalance();
  }, [fetchBalance]);

  // Fetch initial au mount et quand l'auth change
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchBalance();
    } else {
      // Reset si déconnecté
      setBalance(0);
      setLifetimeSpent(0);
      setLifetimeEarned(0);
    }
  }, [isAuthenticated, user, fetchBalance]);

  const value: TokenContextType = {
    balance,
    lifetimeSpent,
    lifetimeEarned,
    isLoading,
    error,
    fetchBalance,
    deductTokens,
    addTokens,
    refreshBalance
  };

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
};

/**
 * Hook pour consommer le TokenContext
 */
export const useTokens = (): TokenContextType => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
};
